// src/services/attendanceService.js - REFACTORED FOR SESSION-BASED ATTENDANCE
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, COLLECTIONS } from './firebase';
import { auth } from './firebase';

export class AttendanceService {
  // Get current user ID
  static getCurrentUserId() {
    return auth.currentUser?.uid;
  }

  // Check if user is authenticated
  static ensureAuthenticated() {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return userId;
  }

  // Create attendance record for a session
  static async createAttendanceRecord(attendanceData) {
    const userId = this.ensureAuthenticated();

    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.ATTENDANCE), {
        ...attendanceData,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        recordedAt: new Date()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating attendance record:', error);
      throw new Error('Failed to create attendance record');
    }
  }

  // Get attendance records with optional filters
  static async getAttendanceRecords(filters = {}) {
    const userId = this.ensureAuthenticated();

    try {
      let q = query(
        collection(db, COLLECTIONS.ATTENDANCE),
        where('createdBy', '==', userId),
        orderBy('recordedAt', 'desc')
      );

      // Apply filters
      if (filters.sessionId) {
        q = query(
          collection(db, COLLECTIONS.ATTENDANCE),
          where('sessionId', '==', filters.sessionId),
          where('createdBy', '==', userId),
          orderBy('recordedAt', 'desc')
        );
      }
      
      if (filters.subject) {
        q = query(q, where('subject', '==', filters.subject));
      }
      
      if (filters.grade) {
        q = query(q, where('grades', 'array-contains', filters.grade));
      }

      if (filters.dateFrom && filters.dateTo) {
        q = query(q, 
          where('recordedAt', '>=', Timestamp.fromDate(new Date(filters.dateFrom))),
          where('recordedAt', '<=', Timestamp.fromDate(new Date(filters.dateTo)))
        );
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sessionDate: doc.data().sessionDate?.toDate ? doc.data().sessionDate.toDate() : doc.data().sessionDate,
        recordedAt: doc.data().recordedAt?.toDate ? doc.data().recordedAt.toDate() : doc.data().recordedAt,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      throw new Error('Failed to fetch attendance records');
    }
  }

  // Get single attendance record
  static async getAttendanceRecord(recordId) {
    this.ensureAuthenticated();

    try {
      const docRef = doc(db, COLLECTIONS.ATTENDANCE, recordId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          sessionDate: data.sessionDate?.toDate ? data.sessionDate.toDate() : data.sessionDate,
          recordedAt: data.recordedAt?.toDate ? data.recordedAt.toDate() : data.recordedAt,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        };
      } else {
        throw new Error('Attendance record not found');
      }
    } catch (error) {
      console.error('Error fetching attendance record:', error);
      throw new Error('Failed to fetch attendance record');
    }
  }

  // Update attendance record
  static async updateAttendanceRecord(recordId, updates) {
    const userId = this.ensureAuthenticated();

    try {
      const recordRef = doc(db, COLLECTIONS.ATTENDANCE, recordId);
      
      // Verify user owns the record before updating
      const recordDoc = await getDoc(recordRef);
      if (!recordDoc.exists()) {
        throw new Error('Attendance record not found');
      }
      
      const recordData = recordDoc.data();
      if (recordData.createdBy !== userId) {
        throw new Error('Permission denied: You can only edit records you created');
      }

      await updateDoc(recordRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating attendance record:', error);
      throw error;
    }
  }

  // Delete attendance record
  static async deleteAttendanceRecord(recordId) {
    const userId = this.ensureAuthenticated();

    try {
      const recordRef = doc(db, COLLECTIONS.ATTENDANCE, recordId);
      
      // Verify user owns the record before deleting
      const recordDoc = await getDoc(recordRef);
      if (!recordDoc.exists()) {
        throw new Error('Attendance record not found');
      }
      
      const recordData = recordDoc.data();
      if (recordData.createdBy !== userId) {
        throw new Error('Permission denied: You can only delete records you created');
      }

      await deleteDoc(recordRef);
    } catch (error) {
      console.error('Error deleting attendance record:', error);
      throw error;
    }
  }

  // Get attendance analytics for sessions
  static async getAttendanceAnalytics(filters = {}) {
    const userId = this.ensureAuthenticated();

    try {
      const records = await this.getAttendanceRecords(filters);
      
      if (records.length === 0) {
        return {
          totalRecords: 0,
          averageAttendance: 0,
          totalExpected: 0,
          totalActual: 0,
          attendanceRate: 0,
          engagementStats: { H: 0, M: 0, L: 0 },
          behaviourStats: { E: 0, G: 0, S: 0, N: 0 },
          attendanceTrend: []
        };
      }

      // Calculate analytics
      let totalExpected = 0;
      let totalActual = 0;
      const engagementStats = { H: 0, M: 0, L: 0 };
      const behaviourStats = { E: 0, G: 0, S: 0, N: 0 };

      records.forEach(record => {
        if (record.stats?.expectedLearners) {
          totalExpected += record.stats.expectedLearners;
        }
        if (record.stats?.actualLearners) {
          totalActual += record.stats.actualLearners;
        }

        // Process attendance data for engagement and behaviour
        if (record.attendanceData) {
          record.attendanceData.forEach(learner => {
            if (learner.engagement && engagementStats[learner.engagement] !== undefined) {
              engagementStats[learner.engagement]++;
            }
            if (learner.behaviour && behaviourStats[learner.behaviour] !== undefined) {
              behaviourStats[learner.behaviour]++;
            }
          });
        }
      });

      const attendanceRate = totalExpected > 0 ? (totalActual / totalExpected) * 100 : 0;
      const averageAttendance = records.length > 0 ? totalActual / records.length : 0;

      // Build attendance trend (last 10 records)
      const attendanceTrend = records.slice(0, 10).map(record => ({
        date: record.recordedAt || record.sessionDate,
        rate: record.stats?.attendanceRate || 0
      })).reverse();

      return {
        totalRecords: records.length,
        averageAttendance: Math.round(averageAttendance),
        totalExpected,
        totalActual,
        attendanceRate: Math.round(attendanceRate),
        engagementStats,
        behaviourStats,
        attendanceTrend
      };
    } catch (error) {
      console.error('Error getting attendance analytics:', error);
      throw new Error('Failed to get attendance analytics');
    }
  }

  // File upload for attendance records
  static async uploadAttendanceFile(file, recordId, fileName = null) {
    this.ensureAuthenticated();

    try {
      const sanitizedFileName = fileName || `attendance_${Date.now()}.${file.name.split('.').pop()}`;
      const path = `attendance-records/${this.getCurrentUserId()}/${recordId}/${sanitizedFileName}`;
        
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        fileName: sanitizedFileName,
        originalName: file.name,
        path: path,
        uploadedAt: new Date()
      };
    } catch (error) {
      console.error('Error uploading attendance file:', error);
      throw new Error('Failed to upload attendance file');
    }
  }

  // Delete attendance file
  static async deleteAttendanceFile(filePath) {
    this.ensureAuthenticated();
    
    try {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting attendance file:', error);
      throw new Error('Failed to delete attendance file');
    }
  }

  // Create attendance record from session data
  static async createAttendanceFromSession(sessionId, attendanceData) {
    const userId = this.ensureAuthenticated();

    try {
      // Import SessionService to get session data
      const { SessionService } = await import('./sessionService');
      const session = await SessionService.getSession(sessionId);

      // Calculate attendance statistics
      const expectedLearners = attendanceData.length;
      const actualLearners = attendanceData.filter(learner => 
        learner.attendance === 'Present' || learner.attendance === 'Late'
      ).length;
      const attendanceRate = expectedLearners > 0 ? (actualLearners / expectedLearners) * 100 : 0;

      const recordData = {
        sessionId: sessionId,
        sessionTitle: session.title,
        subject: session.subject,
        grades: session.grades,
        sessionDate: session.sessionDate,
        sessionContext: session.sessionContext,
        attendanceData: attendanceData,
        stats: {
          expectedLearners,
          actualLearners,
          attendanceRate: Math.round(attendanceRate),
          presentCount: attendanceData.filter(l => l.attendance === 'Present').length,
          lateCount: attendanceData.filter(l => l.attendance === 'Late').length,
          absentCount: attendanceData.filter(l => l.attendance === 'Absent').length,
          excusedCount: attendanceData.filter(l => l.attendance === 'Excused').length
        }
      };

      return await this.createAttendanceRecord(recordData);
    } catch (error) {
      console.error('Error creating attendance from session:', error);
      throw new Error('Failed to create attendance record from session');
    }
  }
}